import apiClient from "@/api/client";
import { db, type NotePayloadBody, type NoteQueued } from "@/db";

const BACKOFF_STEPS_MS = [0, 5_000, 30_000, 300_000, 1_800_000]; // 0s, 5s, 30s, 5m, 30m
const MAX_RETRIES = BACKOFF_STEPS_MS.length;
const LOG_RETENTION = 500;

// Represents the shape of old DB records before schema migrations
type LegacyNoteQueued = {
  local_id?: string;
  localId?: string;
  created_at_client?: string;
  created_at?: string;
  retry_count?: number;
  queue_status?: "pending" | "failed";
  status?: string;
  payload: NoteQueued["payload"];
  udprn: number;
  id?: number;
};

type ApiError = {
  response?: { status?: number; data?: { detail?: string } };
  message?: string;
};

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const getBackoffMs = (retryCount: number) =>
  BACKOFF_STEPS_MS[Math.min(retryCount, BACKOFF_STEPS_MS.length - 1)];

const shouldDelay = (note: NoteQueued) => {
  if (!note.last_attempt_at) return false;
  const nextAllowed =
    new Date(note.last_attempt_at).getTime() + getBackoffMs(note.retry_count ?? 0);
  return Date.now() < nextAllowed;
};

const isNetworkError = (err: unknown) => !(err as Record<string, unknown>)?.response;

async function logSyncEvent({
  local_id,
  udprn,
  event_type,
  retry_count,
  http_status,
  error_message,
}: {
  local_id: string;
  udprn: number;
  event_type: "attempt" | "success" | "failed_permanent";
  retry_count: number;
  http_status?: number | null;
  error_message?: string | null;
}) {
  try {
    await db.syncLogs.add({
      local_id,
      udprn,
      event_type,
      retry_count,
      http_status: http_status ?? null,
      error_message: error_message ?? null,
      timestamp: new Date().toISOString(),
    });

    const count = await db.syncLogs.count();
    if (count > LOG_RETENTION) {
      const overflow = count - LOG_RETENTION;
      const old = await db.syncLogs.orderBy("id").limit(overflow).toArray();
      await db.syncLogs.bulkDelete(old.map((l) => l.id!).filter(Boolean));
    }
  } catch {
    // ignore logging errors
  }
}

export async function getNotesQueueCounts() {
  const [pendingCount, failedCount] = await Promise.all([
    db.notesQueue.where("queue_status").equals("pending").count(),
    db.notesQueue.where("queue_status").equals("failed").count(),
  ]);

  return { pendingCount, failedCount };
}

export async function syncQueuedNotes({
  api = apiClient,
  includeFailed = true,
  stopOnNetworkError = true,
}: {
  api?: typeof apiClient;
  includeFailed?: boolean;
  stopOnNetworkError?: boolean;
} = {}) {
  const queued = await db.notesQueue.toArray();
  const normalized: NoteQueued[] = [];

  for (const rawNote of queued) {
    const note = rawNote as LegacyNoteQueued;
    if (note.local_id) {
      normalized.push({
        ...rawNote,
        queue_status: (note.queue_status ?? note.status ?? "pending") as "pending" | "failed",
      });
      continue;
    }

    // migrate old shape that lacked local_id / used different field names
    const local_id = note.local_id || note.localId || "";
    const created_at_client = note.created_at_client || note.created_at || new Date().toISOString();

    const newId = await db.notesQueue.add({
      ...rawNote,
      id: undefined,
      local_id,
      created_at_client,
      retry_count: note.retry_count ?? 0,
      queue_status: (note.queue_status ?? note.status ?? "pending") as "pending" | "failed",
    } as NoteQueued);

    const reloaded = await db.notesQueue.get(newId);
    if (reloaded) {
      normalized.push(reloaded);
    }
  }

  const sorted = normalized.sort((a, b) =>
    String(a.created_at_client ?? "").localeCompare(String(b.created_at_client ?? ""))
  );

  for (const note of sorted) {
    if (note.queue_status === "failed" && !includeFailed) {
      continue;
    }

    if (shouldDelay(note)) {
      continue;
    }

    const retryCount = note.retry_count ?? 0;
    const attemptTs = new Date().toISOString();

    const endpoint = note.payload?.endpoint ?? `address/${note.udprn}/notes/`;
    const payloadBody = (note.payload?.body ?? note.payload ?? {}) as NotePayloadBody;
    const finalPayload = {
      ...payloadBody,
      client_created_at: payloadBody.client_created_at ?? note.created_at_client,
      local_id: payloadBody.local_id ?? note.local_id,
    };

    const key = note.local_id;

    await db.notesQueue.update(key, {
      last_attempt_at: attemptTs,
      queue_status: "pending",
      error: null,
      retry_count: retryCount,
    });

    await logSyncEvent({
      local_id: finalPayload.local_id,
      udprn: note.udprn,
      event_type: "attempt",
      retry_count: retryCount,
    });

    try {
      await api.post(endpoint, finalPayload);
      await db.notesQueue.delete(key);

      await logSyncEvent({
        local_id: finalPayload.local_id,
        udprn: note.udprn,
        event_type: "success",
        retry_count: retryCount,
      });
    } catch (err) {
      const e = err as ApiError;
      const network = isNetworkError(err);
      const status = e.response?.status ?? null;
      const message =
        e.response?.data?.detail ??
        e.message ??
        (typeof err === "string" ? err : "Sync failed");

      const nextRetry = retryCount + 1;
      const exceeded = nextRetry >= MAX_RETRIES;
      const fatal = (status && status >= 400 && status < 500) || exceeded;
      const newStatus = fatal ? "failed" : "pending";

      await db.notesQueue.update(key, {
        retry_count: nextRetry,
        last_attempt_at: attemptTs,
        queue_status: newStatus,
        error: message,
      });

      if (fatal) {
        await logSyncEvent({
          local_id: finalPayload.local_id,
          udprn: note.udprn,
          event_type: "failed_permanent",
          retry_count: nextRetry,
          http_status: status,
          error_message: message,
        });
      }

      if (network && stopOnNetworkError) {
        break;
      }

      if (!network && !fatal) {
        const delay = getBackoffMs(nextRetry);
        if (delay > 0) {
          await sleep(delay);
        }
      }
    }
  }

  return getNotesQueueCounts();
}
